import {H1} from '@jetbrains/ring-ui/components/heading/heading'
import {memo, Suspense} from 'react'

import TransformText from '../../../containers/TransformText'
import {PlaceIdList} from '../../../plugins/plugins.types'
import type {ChangeId} from '../../../types'
import Avatar, {Size} from '../../common/Avatar/Avatar.container'
import Plugin from '../../common/Plugin/Plugin'
import UncommitedChangeLabel from '../../common/UncommitedChangeLabel/UncommitedChangeLabel'
import ChangeAuthor from '../../packages/Changes/ChangeAuthor/ChangeAuthor'
import ChangeRevisionLink from '../../packages/Changes/ChangeRevision/ChangeRevision'
import {getChange, getVcsRoot, getVcsRootInstance} from '../../packages/Changes/Changes.selectors'
import {getSingleUser} from '../../packages/Changes/Changes.utils'

import {usePageTitle} from './ChangePage.hooks'
import ChangeStatus from './ChangeStatus/ChangeStatus'
import ChangeVcsRoots from './ChangeVcsRoots/ChangeVcsRoots'

import styles from './ChangePage.css'

import {useAppSelector} from 'src/hooks/react-redux'

type Props = {
  changeId: ChangeId
}

function ChangePageHeader({changeId}: Props) {
  const pageTitle = usePageTitle(changeId)
  const personal = useAppSelector(state => getChange(state, changeId)?.personal)
  const comment = useAppSelector(state => getChange(state, changeId)?.comment)
  const username = useAppSelector(state => getChange(state, changeId)?.username)
  const version = useAppSelector(state => getChange(state, changeId)?.version)
  const parentRevisions = useAppSelector(state => getChange(state, changeId)?.parentRevisions)
  const date = useAppSelector(state => getChange(state, changeId)?.date)
  const vcsRootInstanceId = useAppSelector(state => getChange(state, changeId)?.vcsRootInstance)
  const user = useAppSelector(state => getSingleUser(getChange(state, changeId)))

  const vcsRootId = useAppSelector(
    state => getVcsRootInstance(state, vcsRootInstanceId)?.['vcs-root'],
  )
  const vcsRootExists = useAppSelector(state => getVcsRoot(state, vcsRootId) != null)

  // const hasPermissionEditChange = usePermission(Permission.EDIT_VCS_MODIFICATION)
  // const editComment = () => BS && BS.EditModificationDescriptionDialog.showDialog()

  return (
    <div className={styles.header}>
      <div className={styles.headingWrapper}>
        <H1 className={styles.heading} id="change-page-title">
          <Avatar user={user} vcsUserName={username} className={styles.avatar} size={Size.Size28} />
          <span className={styles.title}>{pageTitle}</span>
        </H1>
        {personal === true && <UncommitedChangeLabel className={styles.uncommitedChangeLabel} />}
      </div>
      <Plugin placeId={PlaceIdList.SAKURA_BEFORE_CONTENT} />
      <table className={styles.table}>
        <tbody>
          <tr>
            <th>{'Comment'}</th>
            <td>
              <TransformText
                className={styles.comment}
                changeId={changeId}
                personal={personal}
                text={comment != null && comment !== '' ? comment : 'No comment'}
              />
              {/*{!personal && hasPermissionEditChange ? (*/}
              {/*  <IconButton*/}
              {/*    className={styles.editCommentButton}*/}
              {/*    icon="pencil"*/}
              {/*    onClick={editComment}*/}
              {/*  >*/}
              {/*    {'Edit'}*/}
              {/*  </IconButton>*/}
              {/*) : null}*/}
            </td>
          </tr>
          {version != null || parentRevisions?.item != null ? (
            <tr>
              <th>{'Revision'}</th>
              <td className={styles.revision}>
                <ChangeRevisionLink
                  className={styles.revisionLink}
                  date={date}
                  version={version}
                  personal={personal}
                  withCopyButton
                />
                {(parentRevisions?.item ?? []).map(revision => (
                  <div key={revision}>
                    {`Parent: `}
                    <ChangeRevisionLink
                      className={styles.revisionLink}
                      version={revision}
                      withCopyButton
                    />
                  </div>
                ))}
              </td>
            </tr>
          ) : null}
          <tr>
            <th>{'Author'}</th>
            <td>
              <Suspense>
                <ChangeAuthor changeId={changeId} inline />
              </Suspense>
            </td>
          </tr>
          {vcsRootExists ? (
            <tr>
              <th>{'VCS Roots'}</th>
              <td>
                <ChangeVcsRoots changeId={changeId} />
              </td>
            </tr>
          ) : null}
          <tr>
            <th>{'Status'}</th>
            <td>
              <ChangeStatus changeId={changeId} />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

export default memo(ChangePageHeader)
